import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import {
  ConfirmPasswordInput,
  EmailInput,
  OTPInput,
  PasswordInput,
} from "../UIElements/inputs";
import { useNavigate } from "react-router-dom";
import { PATH_AUTH } from "../../routes/path";
import { useTheme } from "@mui/material/styles";
import { ResetPasswordPropType } from "../../interface/auth";

import {
  forgotPasswordSchemaOne,
  forgotPasswordSchemaTwo,
  passwordSchema,
} from "../../validate/authentication";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { apiForgotPasswordEmail, apiResetPassword } from "../../api/auth";

export const ResetPasswordDetailsOne = ({
  pageHandler,
  changeValues,
}: ResetPasswordPropType) => {
  const { showNotification } = useNotificationService();

  // set up validation
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(forgotPasswordSchemaOne),
  });
  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiForgotPasswordEmail,
    onSuccess: (data) => {
      
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Otp send to email", "success");
        changeValues((prevState) => ({
          ...prevState,
          token: data?.data?.data?.token ?? "",
        }));
        pageHandler();
      }
    },
    onError: (error) => {
      console.error("Reset password process failed", error);
      // Handle error
      showNotification("Reset password process failed", "error");
    },
  });
  const handleOnSubmit = async (value: any) => {
    const { email } = value;
    changeValues((prevState) => ({
      ...prevState,
      email: email,
    }));
    mutation.mutate({ email });
  };
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <EmailInput
          errors={errors}
          labelText="Email"
          placeholder="Email Address"
          register={register}
        />
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 4 }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              marginLeft: "auto",
              width: "100%",
              fontWeight: 700,
              borderRadius: "12px",
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};

export const ResetPasswordDetailsTwo = ({
  pageHandler,
  values,
  changeValues,
  prevPage,
}: ResetPasswordPropType) => {
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  // set up validation
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(forgotPasswordSchemaTwo),
  });
  const handleOnSubmit = async (value: any) => {
    const { code } = value;
    const stringNumber = code;
    const current_code = stringNumber.replace(/-/g, "");
    changeValues((prevState) => ({
      ...prevState,
      code: current_code,
    }));
    pageHandler();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <OTPInput
          labelText="OTP"
          placeholder="0 - 0 - 0 - 0"
          errors={errors}
          register={register}
        />

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"end"}
          sx={{ my: "1.5rem" }}
        >
          <LoadingButton
            loading={isLoading}
            variant="contained"
            type="submit"
            sx={{
              marginLeft: "auto",
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          {/* <Typography
            onClick={handleResend}
            sx={{
              fontSize: "16px",
              lineHeight: "27px",
              color: theme.palette.text.disabled,
            }}
          >
            Resent OTP? (2:21){" "}
          </Typography> */}
          <Typography
            onClick={prevPage}
            sx={{
              fontSize: "16px",
              lineHeight: "27px",
              cursor: "pointer",
              color: theme.palette.text.disabled,
            }}
          >
            Change email
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export const ResetPasswordDetailsThree = ({
  pageHandler,
  values,
  changeValues,
  prevPage,
}: ResetPasswordPropType) => {
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();

  // set up validation
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(passwordSchema),
  });
  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiResetPassword,
    onSuccess: (data) => {
      
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(
          data?.data?.message ?? "Password reset, kindly login",
          "success",
        );
        changeValues((prevState) => ({
          ...prevState,
          token: data?.data?.data?.token ?? "",
        }));
        navigate(PATH_AUTH.signIn, { replace: true });
      }
    },
    onError: (error) => {
      console.error("Reset password process failed", error);
      // Handle error
      showNotification("Reset password process failed", "error");
    },
  });

  const handleOnSubmit = async (value: any) => {
    const { password } = value;
    const data = { password: password, email: values?.email, otp: values?.code, token: values?.token };
    
    mutation.mutate(data)
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <PasswordInput
          errors={errors}
          placeholder="Enter password"
          labelText="New Password"
          register={register}
        />
        <ConfirmPasswordInput
          errors={errors}
          labelText="Confirm Password"
          placeholder="Enter password"
          register={register}
        />
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"end"}
          sx={{ my: 4 }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};
